/**
 * Admin Dashboard — DocuMind Summit 2026
 *
 * Hidden page at /admin/analytics — not linked from nav.
 * Navigate directly to view post-event stats and system health.
 *
 * Tab 1 — Analytics: demo runs, CSAT, flow funnel, module heatmap
 * Tab 2 — System Health: Bedrock, KB, guardrails, market data, graph engine
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box,
  ColumnLayout,
  Container,
  Header,
  SpaceBetween,
  StatusIndicator,
  Table,
  Button,
  Badge,
  Spinner,
  Alert,
  Tabs,
  TextContent,
} from '@cloudscape-design/components';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

async function apiFetch(path) {
  const res = await axios.get(`${API_BASE_URL}${path}`);
  return res.data;
}

// ---------------------------------------------------------------------------
// Shared components
// ---------------------------------------------------------------------------

function StatCard({ label, value, unit, color }) {
  const isLong = typeof value === 'string' && value.length > 6;
  return (
    <Box textAlign="center" padding="l">
      <Box variant="small" color="text-body-secondary" padding={{ bottom: 'xs' }}>
        {label}
      </Box>
      <Box variant={isLong ? 'h2' : 'h1'} color={color || 'text-label'}>
        {value ?? '—'}
      </Box>
      {unit && (
        <Box variant="small" color="text-body-secondary" padding={{ top: 'xxs' }}>
          {unit}
        </Box>
      )}
    </Box>
  );
}

function HealthRow({ label, status, detail }) {
  const type = status === 'ok' || status === 'available' || status === 'healthy'
    ? 'success'
    : status === 'degraded' ? 'warning'
    : 'error';
  return (
    <Box padding={{ vertical: 'xs' }}>
      <ColumnLayout columns={3} variant="text-grid">
        <Box fontWeight="bold">{label}</Box>
        <StatusIndicator type={type}>{status}</StatusIndicator>
        <Box variant="small" color="text-body-secondary">{detail || ''}</Box>
      </ColumnLayout>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Analytics tab
// ---------------------------------------------------------------------------

const FUNNEL_STEPS = ['demo_completed'];

function FunnelBar({ funnel }) {
  const max = Math.max(...FUNNEL_STEPS.map(s => funnel[s] || 0), 1);
  return (
    <SpaceBetween size="s">
      {FUNNEL_STEPS.map(step => {
        const count = funnel[step] || 0;
        const pct = Math.round((count / max) * 100);
        return (
          <div key={step} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 70, textTransform: 'capitalize', fontSize: 13,
              color: 'var(--color-text-body-secondary, #8d99a8)', flexShrink: 0 }}>
              {step}
            </div>
            <div style={{
              flex: 1, height: 20, background: 'var(--color-background-container-content, #1a2332)',
              borderRadius: 4, overflow: 'hidden'
            }}>
              <div style={{
                width: `${Math.max(pct, count > 0 ? 2 : 0)}%`, height: '100%',
                background: '#0972d3', borderRadius: 4, transition: 'width 0.3s ease'
              }} />
            </div>
            <div style={{ width: 30, textAlign: 'right', fontSize: 13, flexShrink: 0 }}>
              {count}
            </div>
          </div>
        );
      })}
    </SpaceBetween>
  );
}

function AnalyticsTab({ data, loading, error }) {
  if (loading && !data) return <Box textAlign="center" padding="xl"><Spinner size="large" /></Box>;
  if (error) return <Alert type="error">{error}</Alert>;
  if (!data) return null;

  const moduleRows = Object.entries(data.module_visits || {})
    .sort((a, b) => b[1] - a[1])
    .map(([module, count]) => ({ module, count }));

  const csatColor = data.csat.score === null ? 'text-body-secondary'
    : data.csat.score >= 4 ? 'text-status-success'
    : 'text-status-warning';

  return (
    <SpaceBetween size="l">
      <Container header={<Header variant="h2">Demo Runs</Header>}>
        <ColumnLayout columns={3} variant="text-grid">
          <StatCard label="Today" value={data.demo_runs.today}
            unit="completed demos" color="text-status-success" />
          <StatCard label="Last 7 days" value={data.demo_runs.last_7_days}
            unit="completed demos" />
          <StatCard label="Total unique sessions" value={data.total_unique_sessions}
            unit="across all events" />
        </ColumnLayout>
      </Container>

      <Container header={<Header variant="h2">CSAT</Header>}>
        <ColumnLayout columns={4} variant="text-grid">
          <StatCard label="Score" color={csatColor}
            value={data.csat.score !== null ? `${data.csat.score} / 5` : 'No data'} />
          <StatCard label="👍 Helpful" value={data.csat.thumbs_up} />
          <StatCard label="👎 Not helpful" value={data.csat.thumbs_down} />
          <StatCard label="Total responses" value={data.csat.total_responses} />
        </ColumnLayout>
      </Container>

      <Container header={<Header variant="h2">Module Engagement</Header>}>
        {moduleRows.length === 0
          ? <Box color="text-body-secondary">No module visits recorded yet.</Box>
          : (
            <Table
              columnDefinitions={[
                { id: 'module', header: 'Module', cell: r => <Badge color="blue">{r.module}</Badge> },
                { id: 'count', header: 'Visits', cell: r => r.count },
              ]}
              items={moduleRows}
              variant="embedded"
              stripedRows
            />
          )
        }
      </Container>

      <Box variant="small" color="text-body-secondary" textAlign="right">
        Total events in DynamoDB: {data.total_events}
      </Box>
    </SpaceBetween>
  );
}

// ---------------------------------------------------------------------------
// System Health tab
// ---------------------------------------------------------------------------

function HealthTab({ health, loading, error }) {
  if (loading && !health) return <Box textAlign="center" padding="xl"><Spinner size="large" /></Box>;
  if (error) return <Alert type="error">{error}</Alert>;
  if (!health) return null;

  const { app, bedrock, graphEngine, guardrails, marketData } = health;

  return (
    <SpaceBetween size="l">

      <Container header={<Header variant="h2">Application</Header>}>
        <SpaceBetween size="xs">
          <HealthRow label="App" status={app?.status || 'unknown'}
            detail={`Strands: ${app?.strands_available ? '✅' : '❌'} | Env: ${app?.environment || '—'}`} />
          <HealthRow label="Time sync" status={bedrock?.is_synchronized ? 'ok' : 'skewed'}
            detail={bedrock?.skew_seconds !== undefined ? `Skew: ${bedrock.skew_seconds}s` : ''} />
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Bedrock</Header>}>
        <SpaceBetween size="xs">
          <HealthRow
            label="Connectivity"
            status={graphEngine?.components?.bedrock_connectivity?.status || 'unknown'}
            detail={graphEngine?.components?.bedrock_connectivity?.response_time
              ? `${Math.round(graphEngine.components.bedrock_connectivity.response_time)}ms`
              : '—'}
          />
          <HealthRow
            label="Model"
            status={graphEngine?.components?.bedrock_connectivity?.status || 'unknown'}
            detail={graphEngine?.model_id || '—'}
          />
          <HealthRow
            label="Knowledge Base"
            status={graphEngine?.components?.bedrock_connectivity?.status || 'unknown'}
            detail={graphEngine?.knowledge_base_id || '—'}
          />
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Graph Engine</Header>}>
        <SpaceBetween size="xs">
          <HealthRow
            label="Status"
            status={graphEngine?.components?.graph_engine?.status || 'unknown'}
            detail={`WebSocket connections: ${graphEngine?.components?.websocket_manager?.total_connections ?? '—'}`}
          />
          <HealthRow
            label="Active sessions"
            status="ok"
            detail={`${graphEngine?.components?.websocket_manager?.active_sessions ?? '—'} active`}
          />
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Guardrails</Header>}>
        <SpaceBetween size="xs">
          <HealthRow
            label="Status"
            status={guardrails?.status || 'unknown'}
            detail={`ID: ${guardrails?.guardrail_id || '—'} v${guardrails?.guardrail_version || '—'}`}
          />
          <HealthRow
            label="Knowledge Base"
            status={guardrails?.kb_connectivity?.startsWith('ok') ? 'ok' : 'error'}
            detail={guardrails?.kb_connectivity || '—'}
          />
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Market Data</Header>}>
        <HealthRow
          label="Price data"
          status={marketData?.status || 'unknown'}
          detail={marketData?.record_count ? `${marketData.record_count} records` : '—'}
        />
      </Container>

      <Box variant="small" color="text-body-secondary" textAlign="right">
        Checked at: {new Date().toLocaleTimeString()}
      </Box>
    </SpaceBetween>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function AnalyticsDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('analytics');

  // Start load immediately — avoids blank content flash on navigation
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [analyticsData, appHealth, bedrockHealth, graphHealth, guardrailsHealth, marketHealth] =
        await Promise.allSettled([
          apiFetch('/api/v2/analytics/summary'),
          apiFetch('/health'),
          apiFetch('/time-sync'),
          apiFetch('/api/v2/health'),
          apiFetch('/api/v2/guardrails/health'),
          apiFetch('/api/v2/market-data/health'),
        ]);

      if (analyticsData.status === 'fulfilled') setAnalytics(analyticsData.value);
      else setError(analyticsData.reason?.message || 'Failed to load analytics');

      setHealth({
        app: appHealth.status === 'fulfilled' ? appHealth.value : null,
        bedrock: bedrockHealth.status === 'fulfilled' ? bedrockHealth.value : null,
        graphEngine: graphHealth.status === 'fulfilled' ? graphHealth.value : null,
        guardrails: guardrailsHealth.status === 'fulfilled' ? guardrailsHealth.value : null,
        marketData: marketHealth.status === 'fulfilled' ? marketHealth.value : null,
      });
    } catch (e) {
      setError(e.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <SpaceBetween size="l">
        <Header
          variant="h1"
          actions={<Button iconName="refresh" onClick={load} loading={loading}>Refresh</Button>}
          description={analytics ? `Last updated: ${new Date(analytics.generated_at).toLocaleString()}` : ''}
        >
          Admin Dashboard
        </Header>

        <Tabs
          activeTabId={activeTab}
          onChange={({ detail }) => setActiveTab(detail.activeTabId)}
          tabs={[
            {
              id: 'analytics',
              label: '📊 Analytics',
              content: <AnalyticsTab data={analytics} loading={loading} error={error} />,
            },
            {
              id: 'health',
              label: '🩺 System Health',
              content: <HealthTab health={health} loading={loading} error={null} />,
            },
          ]}
        />
      </SpaceBetween>
  );
}
